import { pxToRem } from '@back-office/core';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import InputAdornment from '@material-ui/core/InputAdornment';
import { useTheme } from '@material-ui/core/styles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import TextFields from '@material-ui/core/TextField';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import SearchIcon from '@material-ui/icons/Search';
import React, { ChangeEvent, FC, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { animated, useSpring } from 'react-spring';

const useStyles = makeStyles(
  (theme) => ({
    root: {
      '&:hover:not($disabled):not($focused):not($error) $notchedOutline': {
        borderColor: theme.palette.common.ceriseRed,
        color: theme.palette.common.ceriseRed,
      },
    },
    adornment: {
      color: (props: GcpSearchBarProps) =>
        props.isMouseOver
          ? theme.palette.common.ceriseRed
          : theme.palette.common.hitGray,
      cursor: 'pointer',
    },
    notchedOutline: {},
    field: {
      '& > .MuiInputLabel-formControl': {
        marginLeft: 'auto',
        marginRight: 'auto',
        textAlign: 'center',
        top: '-5%',
      },
    },
  }),
  { name: 'GcpSearchBar' }
);

export interface GcpSearchBarProps {
  openSearchBar: boolean;
  isMouseOver: boolean;
  handleOnClickAway: () => void;
  handleOnClick: () => void;
  handleOnMouseEnter: () => void;
  handleOnMouseLeave: () => void;
  handleOnChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleOnKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  handleOnGo: () => void;
  value: string;
}

const GcpSearchBar: FC<GcpSearchBarProps> = (props) => {
  const classes = useStyles(props);
  const { t } = useTranslation();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('lg'));
  const expanded = matches ? '25ch' : '8ch';
  const {
    openSearchBar,
    handleOnClickAway,
    isMouseOver,
    handleOnClick,
    handleOnMouseEnter,
    handleOnMouseLeave,
    handleOnChange,
    handleOnGo,
    handleOnKeyDown,
    value,
  } = props;

  const expand = useSpring({
    width: openSearchBar ? expanded : pxToRem(48),
  });

  const fade = useSpring({
    opacity: !openSearchBar ? 1 : 0,
  });

  return (
    <animated.div style={expand}>
      <ClickAwayListener onClickAway={handleOnClickAway}>
        <TextFields
          fullWidth
          InputLabelProps={{
            style: {
              color: isMouseOver ? '#D82662' : '#ACB5BD',
              fontSize: pxToRem(24),
            },
          }}
          InputProps={{
            endAdornment: openSearchBar && (
              <InputAdornment
                disableTypography
                className={classes.adornment}
                position="end"
                onClick={handleOnGo}
              >
                GO
              </InputAdornment>
            ),
            classes: {
              root: classes.root,
              notchedOutline: classes.notchedOutline,
            },
          }}
          className={classes.field}
          label={
            !openSearchBar && (
              <animated.div style={fade}>
                <SearchIcon />
              </animated.div>
            )
          }
          placeholder={t('transaction_list.search_transaction')}
          type="text"
          value={value}
          variant="outlined"
          onChange={handleOnChange}
          onClick={handleOnClick}
          onKeyDown={handleOnKeyDown}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
        />
      </ClickAwayListener>
    </animated.div>
  );
};

export default GcpSearchBar;
